import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/users/users.service';
import { AdminService } from 'src/app/admin/admin.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import * as moment from 'moment';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';

@Component({
	selector: 'app-reveal-password-requests',
	templateUrl: './reveal-password-requests.component.html',
	styleUrls: ['./reveal-password-requests.component.css']
})
export class RevealPasswordRequestsComponent implements OnInit {
	activeTab: 'site' | 'device' = 'site';
	moment = moment;
	selectedRequestId: number;
	selectedRequestType: 'site' | 'device';
	rejectReason: string = '';
	fullRejectReason: string = '';
	
	// Raw request data
	siteRequests: any[] = [];
	deviceRequests: any[] = [];
	
	// Table data
	siteTableData: TableData[] = [];
	deviceTableData: TableData[] = [];
	
	// Table configurations
	siteTableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 20,
		draggable: true,
		hideNoData: false,
		csvFileHeaderInfo: [this.translateService.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')],
		fileName: this.translateService.instant('password_reveal.site_password_requests'),
	};
	
	deviceTableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 20,
		draggable: true,
		hideNoData: false,
		csvFileHeaderInfo: [this.translateService.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')],
		fileName: this.translateService.instant('password_reveal.device_password_requests'),
	};

	STATUS_OPTIONS = [
		{ label: this.translateService.instant('password_reveal.status_approved'), value: this.translateService.instant('password_reveal.status_approved') },
		{ label: this.translateService.instant('password_reveal.status_rejected'), value: this.translateService.instant('password_reveal.status_rejected') },
		{ label: this.translateService.instant('password_reveal.status_pending'), value: this.translateService.instant('password_reveal.status_pending') }
	]
	
	siteColumnConfig: ColumnsConfig[] = [];
	deviceColumnConfig: ColumnsConfig[] = [];

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		request_time: TimeSource.PcTime,
		processed_time: TimeSource.PcTime,
	};

	@ViewChild("approveConfirmModal") approveConfirmModal: ModalComponent;
	@ViewChild("rejectConfirmModal") rejectConfirmModal: ModalComponent;

	constructor(
		private router: Router,
		private translateService: TranslateService,
		public usersService: UsersService,
		private adminService: AdminService,
		private sideMenuService: SideMenuService,
		private notificationMessage: NotificationMessageService,
		private commonService: CommonService,
	) { }

	ngOnInit() {
		this.sideMenuService.hide();
		// Check if user is Super Admin
		if (!this.usersService.hasAccessFunction('reveal_password_management')) {
			this.router.navigate(['unauthorized']);
			return;
		}

		this.initializeColumns();
		this.loadSiteRequests();
	}

	initializeColumns() {
		const timezoneOffset = this.getTimezoneOffset();
		
		// Site columns
		this.siteColumnConfig = [
			{ 
				key: 'customer_name', 
				name: this.translateService.instant('password_reveal.customer_name'), 
				type: 'link'
			},
			{ 
				key: 'site_name', 
				name: this.translateService.instant('password_reveal.site_name'), 
				type: 'link'
			},
			{ 
				key: 'requester_name', 
				name: this.translateService.instant('password_reveal.requested_by'), 
				type: 'string' 
			},
			{ 
				key: 'request_time', 
				name: `${this.translateService.instant('password_reveal.request_time')} (${timezoneOffset})`, 
				type: 'date'
			},
			{ 
				key: 'status', 
				name: this.translateService.instant('password_reveal.status'), 
				type: 'string',
				filterType: 'dropdown',
				items: this.STATUS_OPTIONS
			},
			{ 
				key: 'processed_time', 
				name: `${this.translateService.instant('password_reveal.approve_reject_time')} (${timezoneOffset})`, 
				type: 'date' 
			},
			{ 
				key: 'reject_reason', 
				name: this.translateService.instant('password_reveal.reject_reason'), 
				type: 'string' 
			},
			{ 
				key: 'actions', 
				name: '', 
				type: 'dropdown' 
			}
		];

		// Device columns - reordered as requested
		this.deviceColumnConfig = [
			{ 
				key: 'device',
				name: 'Device',
				type: 'link'
			},
			{ 
				key: 'customer_name',
				name: this.translateService.instant('password_reveal.customer_name'),
				type: 'link'
			},
			{ 
				key: 'site_name',
				name: this.translateService.instant('password_reveal.site_name'),
				type: 'link'
			},
			{ 
				key: 'requester_name',
				name: this.translateService.instant('password_reveal.requested_by'),
				type: 'string'
			},
			{ 
				key: 'request_time',
				name: `${this.translateService.instant('password_reveal.request_time')} (${timezoneOffset})`,
				type: 'date'
			},
			{ 
				key: 'status',
				name: this.translateService.instant('password_reveal.status'),
				type: 'string',
				filterType: 'dropdown',
				items: this.STATUS_OPTIONS,
			},
			{ 
				key: 'processed_time',
				name: `${this.translateService.instant('password_reveal.approve_reject_time')} (${timezoneOffset})`,
				type: 'date'
			},
			{ 
				key: 'reject_reason',
				name: this.translateService.instant('password_reveal.reject_reason'),
				type: 'string'
			},
			{ 
				key: 'actions',
				name: '',
				type: 'dropdown'
			}
		];
		this.commonService.applyTimeSourceTooltips(this.siteColumnConfig, this.timeSourceByColumn);
		this.commonService.applyTimeSourceTooltips(this.deviceColumnConfig, this.timeSourceByColumn);
	}

	setActiveTab(tab: 'site' | 'device') {
		this.activeTab = tab;
		if (tab === 'site') {
			this.loadSiteRequests();
		} else {
			this.loadDeviceRequests();
		}
	}

	loadSiteRequests() {
		this.adminService.getPasswordRevealRequests({ request_type: 'site' }).subscribe(
			(response: any) => {
				this.siteRequests = (response || []);
				this.siteTableData = this.siteRequests.map(req => this.formatRequestForTable(req, 'site'));
				this.siteTableConfig = {
					hasExport: true,
					hasPagination: true,
					pageSize: 10,
					draggable: true,
					csvFileHeaderInfo: [this.translateService.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')],
					fileName: this.translateService.instant('password_reveal.site_password_requests'),
				};
			}
		);
	}

	loadDeviceRequests() {
		this.adminService.getPasswordRevealRequests({ request_type: 'device' }).subscribe(
			(response: any) => {
				this.deviceRequests = (response || []);
				this.deviceTableData = this.deviceRequests.map(req => this.formatRequestForTable(req, 'device'));
				this.deviceTableConfig = {
					hasExport: true,
					hasPagination: true,
					pageSize: 10,
					draggable: true,
					csvFileHeaderInfo: [this.translateService.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')],
					fileName: this.translateService.instant('password_reveal.device_password_requests'),
				};
			}
		);
	}

	formatRequestForTable(request: any, type: 'site' | 'device'): TableData {
		const statusLabel = this.getStatusLabel(request.status);
		
		// Build actions dropdown options - only show if request is pending
		const actionOptions = [];
		if (request.status === 'pending') {
			actionOptions.push({
				text: this.translateService.instant('password_reveal.approve'),
				action: () => this.approveRequest(request.id, type)
			});
			actionOptions.push({
				text: this.translateService.instant('password_reveal.reject'),
				action: () => this.rejectRequest(request.id, type)
			});
		}

		const data: TableData = {
			requester_name: { value: `${request.requester_first_name} ${request.requester_last_name}` },
			customer_name: { value: request.customer_name || '-', link: ['/', request.customer_id] },
			site_name: { value: request.site_name || '-', link: ['/', request.customer_id, request.site_id] },
			request_time: { value: request.created_at ? moment(request.created_at).format('MM/DD/YYYY hh:mm A') : '-' },
			processed_time: { value: request.processed_at ? moment(request.processed_at).format('MM/DD/YYYY hh:mm A') : '-' },
			status: { value: statusLabel },
			reject_reason: { value: request.reject_reason },
			actions: { value: null, options: actionOptions }
		};

		if (type === 'device') {
			data.device = { 
				value: request.device_serial_number || '-', 
				link: ['/', request.device_customer_id, request.device_site_id, request.device_mac_address, 'performance'] 
			};
		}

		return data;
	}

	approveRequest(requestId: number, requestType: 'site' | 'device') {
		this.selectedRequestId = requestId;
		this.selectedRequestType = requestType;
		this.approveConfirmModal.show();
	}

	confirmApproveRequest() {
		this.approveConfirmModal.hide();
		this.adminService.approvePasswordRevealRequest(this.selectedRequestId).subscribe((response: any) => {
			if (response.api_status == 1) {
				this.notificationMessage.setMessage('globalSuccessMsg');
				if (this.selectedRequestType === 'site') {
					this.loadSiteRequests();
				} else {
					this.loadDeviceRequests();
				}
			} else {
				this.notificationMessage.setMessage('globalErrMsg');
			}
		});
	}

	rejectRequest(requestId: number, requestType: 'site' | 'device') {
		this.selectedRequestId = requestId;
		this.selectedRequestType = requestType;
		this.rejectReason = ''; // Reset reject reason
		this.rejectConfirmModal.show();
	}

	confirmRejectRequest() {
		// Validate reject reason
		if (!this.rejectReason || this.rejectReason.trim() === '') {
			this.notificationMessage.setMessage('translate|password_reveal.reject_reason_required', {type: 'danger'});
			return;
		}

		// Validate max length
		if (this.rejectReason.trim().length > 250) {
			this.notificationMessage.setMessage('translate|password_reveal.reject_reason_too_long', {type: 'danger'});
			return;
		}

		this.rejectConfirmModal.hide();
		this.adminService.rejectPasswordRevealRequest(this.selectedRequestId, this.rejectReason.trim()).subscribe((response: any) => {
			if (response.api_status == 1) {
				this.notificationMessage.setMessage('globalSuccessMsg');
				this.rejectReason = ''; // Clear after successful submission
				if (this.selectedRequestType === 'site') {
					this.loadSiteRequests();
				} else {
					this.loadDeviceRequests();
				}
			} else {
				this.notificationMessage.setMessage('globalErrMsg');
			}
		});
	}

	getStatusBadgeClass(status: string): string {
		switch (status) {
			case 'pending':
				return 'badge-warning';
			case 'approved':
				return 'badge-success';
			case 'rejected':
				return 'badge-danger';
			default:
				return 'badge-secondary';
		}
	}

	getStatusLabel(status: string): string {
		return this.translateService.instant(`password_reveal.status_${status}`);
	}

	getTimezoneOffset(): string {
		return this.commonService.showUserTimeZoneReference();
	}
}
